import {
  findNodePath,
  isEditorFocused,
  PlateRenderElementProps,
  setNodes,
} from '@udecode/plate-core';
import { DreamBlock, DreamValue, FormElement as FormElementType } from '@typedream/core';
import { Serializer, FormElement as FormElementComponent } from '@typedream/components';
import React from 'react';
import { pick, isEqual, omit } from 'lodash';
import styled from 'styled-components';
import { Path } from 'slate';
import { getTPlugin, useTPlateEditorRef } from '../../../plate';
import { selectElement, useSelectedElementEntry } from '../../../plugins/elementSelection';
import { isBlockEmpty } from '../../../editorTools/queries';
import { ELEMENT_FORM } from '../../../pluginKeys';
import { FormPluginOptions } from '../types';

const StyledEmptyFormContainer = styled.div`
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const StyledEmptyFormButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  margin: 10px 0px;
  border-radius: 10px;
  background-color: var(--background-button);
  border: 1px solid var(--border-modal);
  box-shadow: var(--shadow-modal);
  transition: all 0.1s;
  color: var(--accent) !important;
  cursor: pointer;

  &:hover {
    border: 1px solid var(--accent) !important;
  }
`;

const StyledOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  cursor: pointer;
  transition: all 0.5s;
`;

function ReadOnlyFormElement(props: PlateRenderElementProps<DreamValue, FormElementType>) {
  const { element, attributes, children, className } = props;

  const editor = useTPlateEditorRef();

  const entry = useSelectedElementEntry();
  const elementPath = findNodePath(editor, element);

  const [formContentValue, setFormContentValue] = React.useState<DreamBlock | { children: [] }>({
    children: [],
  });

  const elementFormID = element?.form_id;
  const isMissingFormContent = elementFormID && isEqual(formContentValue, { children: [] });

  const { options } = getTPlugin<FormPluginOptions>(editor, ELEMENT_FORM);

  React.useEffect(() => {
    if (!elementFormID) return;
    options?.getFormContent(elementFormID).then((v) => {
      setFormContentValue(v[0] || element);
    });
  }, [elementFormID]);

  React.useEffect(() => {
    if (!entry || elementFormID)
      return () => {
        /* do nothing */
      };
    const [, path] = entry || [];

    if (path && elementPath && !Path.equals(path, elementPath))
      return () => {
        /* */
      };

    const handler = (formID: string) => {
      setNodes(
        editor,
        {
          form_id: formID,
        },
        {
          at: path,
        }
      );

      options?.getFormContent(formID).then((v) => {
        setFormContentValue(v[0] || { children: [] });
      });
    };
    editor.emitter.on('popup:on-form-connect', handler);
    return () => editor.emitter.off('popup:on-form-connect', handler);
  }, [entry, editor]);

  if (
    isBlockEmpty(editor, element) &&
    (!elementFormID || isEqual(formContentValue, [{ children: [] }]))
  ) {
    return (
      <FormElementComponent
        {...pick(props, ['element', 'className', 'attributes'])}
        handleFormSubmission={async () => {
          /* do nothing */
        }}
      >
        <StyledEmptyFormContainer {...attributes} contentEditable={false} className={className}>
          {isMissingFormContent && (
            <div
              style={{
                color: 'var(--neutral-600)',
              }}
            >
              This form is empty or missing
            </div>
          )}
          <StyledEmptyFormButton
            onClick={() => {
              if (elementPath && !isEditorFocused(editor)) selectElement(editor, elementPath);
              editor.emitter.emit('popup:toggle-form-connect');
            }}
            type="button"
          >
            {isMissingFormContent ? 'Choose Another Form' : 'Connect Form'}
          </StyledEmptyFormButton>
          <span style={{ display: 'none' }} contentEditable={false}>
            {children}
          </span>
        </StyledEmptyFormContainer>
        <div
          style={{
            position: 'absolute',
            height: 0,
            color: 'transparent',
            outline: 'none',
          }}
        >
          {children}
        </div>
      </FormElementComponent>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
      }}
    >
      {/* overlay */}
      <Serializer
        node={{
          ...(formContentValue as any),
          ...omit(element, ['children']),
        }}
        rootStyle={{ minHeight: 'unset' }}
      />
      <StyledOverlay {...attributes}>
        <div style={{ visibility: 'hidden' }}>{children}</div>
      </StyledOverlay>
    </div>
  );
}

export function FormElement(props: PlateRenderElementProps<DreamValue, FormElementType>) {
  const editor = useTPlateEditorRef();
  const { options } = getTPlugin<FormPluginOptions>(editor, ELEMENT_FORM);

  if (options?.isEditable) {
    return (
      <FormElementComponent
        {...pick(props, ['element', 'className', 'attributes', 'children'])}
        handleFormSubmission={async () => {
          /* do nothing */
        }}
      />
    );
  }

  return <ReadOnlyFormElement {...props} />;
}
